import React, { useState } from 'react';
import { Search, MessageCircle, Rocket, Sparkles, Ticket, Plug, CreditCard, BarChart3, Shield, ArrowRight, LifeBuoy, Mail } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Seo from '../components/Seo';
import { WHATSAPP_URL } from '../lib/contact';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PageHero from '../components/PageHero';
import { HELP_CATEGORIES, POPULAR_ARTICLES } from '../mock';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';

const iconMap = { Rocket, MessageCircle, Sparkles, Ticket, Plug, CreditCard, BarChart3, Shield };

const Ajuda = () => {
  const [q, setQ] = useState('');
  const navigate = useNavigate();
  const filtered = HELP_CATEGORIES.filter((c) => q === '' || c.title.toLowerCase().includes(q.toLowerCase()) || c.desc.toLowerCase().includes(q.toLowerCase()));

  return (
    <>
      <Seo
        title="Central de Ajuda"
        description="Guias, tutoriais e respostas para as principais dúvidas sobre a plataforma CoreDesk. Encontre tudo o que precisa para começar."
        path="/ajuda"
      />
      <Header />
      <PageHero
        eyebrow="Central de Ajuda"
        title="Como podemos |ajudar você?"
        subtitle="Encontre guias, tutoriais e respostas para as dúvidas mais comuns sobre a plataforma CoreDesk."
      >
        <div className="max-w-xl mx-auto relative">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar em toda a base de conhecimento..." className="h-14 pl-12 pr-4 text-base rounded-full shadow-lg" />
        </div>
      </PageHero>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-extrabold text-[#0a0e27] mb-8">Navegue por categoria</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {filtered.map((c) => {
              const Icon = iconMap[c.icon] || MessageCircle;
              return (
                <a key={c.title} href="#" className="group block bg-white rounded-2xl border border-slate-100 p-6 hover:border-[#FF5B24]/40 hover:shadow-xl hover:-translate-y-0.5 transition-all">
                  <div className="w-12 h-12 rounded-xl bg-[#FFF1EA] group-hover:bg-[#FF5B24] flex items-center justify-center transition-colors">
                    <Icon size={22} className="text-[#FF5B24] group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="mt-4 font-extrabold text-lg text-[#0a0e27]">{c.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{c.desc}</p>
                  <div className="mt-4 flex items-center justify-between">
                    <span className="text-xs text-slate-400">{c.count} artigos</span>
                    <ArrowRight size={14} className="text-slate-300 group-hover:text-[#FF5B24] transition-colors group-hover:translate-x-1" />
                  </div>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#FAF6F2]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-extrabold text-[#0a0e27] mb-6">Artigos mais lidos</h2>
          <div className="bg-white rounded-2xl border border-slate-100 divide-y divide-slate-100">
            {POPULAR_ARTICLES.map((a, i) => (
              <a key={i} href="#" className="flex items-center gap-4 p-5 hover:bg-slate-50 transition group">
                <span className="w-8 h-8 rounded-full bg-[#FFF1EA] flex items-center justify-center text-[#FF5B24] font-bold text-sm shrink-0">{i + 1}</span>
                <span className="flex-1 font-medium text-[#0a0e27] group-hover:text-[#FF5B24]">{a}</span>
                <ArrowRight size={16} className="text-slate-300 group-hover:text-[#FF5B24] group-hover:translate-x-1 transition-all" />
              </a>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl bg-gradient-to-br from-[#0a0e27] to-[#1a1f4d] p-10 md:p-14 relative overflow-hidden">
            <div className="absolute -top-20 -right-20 w-72 h-72 bg-[#FF5B24]/25 rounded-full blur-3xl" />
            <div className="relative grid md:grid-cols-[1fr_auto] gap-6 items-center">
              <div>
                <LifeBuoy size={30} className="text-[#FF5B24]" />
                <h3 className="mt-4 text-2xl md:text-3xl font-extrabold text-white">Não encontrou o que precisava?</h3>
                <p className="mt-2 text-white/70">Nosso time de suporte responde em português, no horário comercial, com foco em resolver seu problema.</p>
              </div>
              <div className="flex flex-col sm:flex-row md:flex-col gap-2">
                <Button onClick={() => navigate('/contato')} className="btn-coral h-12 px-6 rounded-full font-semibold gap-1.5">
                  <Mail size={15} /> Abrir chamado
                </Button>
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="h-12 px-6 rounded-full font-semibold gap-1.5 inline-flex items-center justify-center bg-[#25D366] text-white hover:bg-[#22c55e] transition">
                  <MessageCircle size={15} /> Falar no WhatsApp
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default Ajuda;
